import { graphql } from '../gql';
import { useCallback, useContext } from 'react';
import { useApiMutation } from '../query/hooks';
import { SignOutErrorCode } from '../gql/graphql';
import { AuthContext } from '../auth/useAuth';

type OnError = (error?: SignOutErrorCode) => void;

type Result = () => void;

const signOutMutation = graphql(`
	mutation auth_signOut {
		signOut {
			ok
			error {
				code
			}
		}
	}
`);

const useSignOut = (onError: OnError): Result => {
	const { setSignedOut, state } = useContext(AuthContext);

	// We use useApiMutation here which fails if auth is loading, but we only call it when not loading
	const { mutate } = useApiMutation(signOutMutation, {
		onSuccess: (data) => {
			if (data.signOut?.error?.code != null) {
				onError(data.signOut.error.code);
				return;
			}

			if (data.signOut?.ok !== true) {
				onError();
				return;
			}
		},
		onError: () => {
			onError();
		},
		onSettled: () => {
			setSignedOut();
		},
		invalidateQueries: false,
	});

	return useCallback(() => {
		// `useSignOut` does nothing if called while auth is loading
		// We could allow this function to only be used when auth has loaded by throwing an exception
		// but this function is used INSIDE AuthGuard, which provides this protection
		if (!state.isLoading) {
			if (state.isSignedIn) {
				mutate({});
			} else {
				setSignedOut();
			}
		}
	}, [mutate, setSignedOut, state]);
};

export { useSignOut };
